// Contract ABIs
//
// NOTE: The strings below are the EXACT names compiled into the deployed
// Solidity contracts (which use "market" vocabulary throughout, e.g.
// "createMarket", "getMarketByTweetHash", "closeMarket", "MarketCreated").
// They are hashed (keccak256) by wagmi/viem to compute function selectors and
// event topics, so the runtime value of each string cannot change without a
// new contract deploy. Callers should import the ABI_FN_*/ABI_EVENT_* wrapper
// constants below instead of hard-coding these strings directly.
import { parseAbi } from 'viem';

export const AAFactoryABI = [
  {
    "type": "function",
    "name": "createMarket",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "address",
        "internalType": "address"
      }
    ],
    "stateMutability": "payable"
  },
  {
    "type": "function",
    "name": "getMarketByTweetHash",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "address",
        "internalType": "address"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "getAllMarkets",
    "inputs": [],
    "outputs": [
      {
        "name": "",
        "type": "address[]",
        "internalType": "address[]"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "isTweetHashUsed",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "bool",
        "internalType": "bool"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "tweetHashExists",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "bool",
        "internalType": "bool"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "relaunch",
    "inputs": [
      {
        "name": "_parentMarket",
        "type": "address",
        "internalType": "address"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "address",
        "internalType": "address"
      }
    ],
    "stateMutability": "payable"
  },
  {
    "type": "function",
    "name": "quoteRelaunch",
    "inputs": [
      {
        "name": "_parentMarket",
        "type": "address",
        "internalType": "address"
      }
    ],
    "outputs": [
      { "name": "cost", "type": "uint256", "internalType": "uint256" },
      { "name": "newEpoch", "type": "uint256", "internalType": "uint256" },
      { "name": "maxRelaunch", "type": "uint256", "internalType": "uint256" }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "getMarketByTweetHashAndEpoch",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      },
      {
        "name": "_epoch",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "address",
        "internalType": "address"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "tweetHashMaxEpoch",
    "inputs": [
      {
        "name": "_tweetHash",
        "type": "string",
        "internalType": "string"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "stateMutability": "view"
  }
];

// Child contract ABI - one per market, no marketId param (JSON format for wagmi/viem)
// Keep tuple field order in sync with shared/abi/AAMarket-getMarket.json (sync-market-state.js).
export const AAMarketABI = [
  /**
   * Solidity signature:
   *   (address, string, uint256, address, uint256, uint256, uint256, uint256, uint256,
   *    uint256, uint256, uint256, uint8, bool, bool)
   *
   * This MUST be declared as 15 separate outputs — NOT a single `tuple` wrapper. Multi-return
   * encoding at the top level is a flat head/tail layout, whereas a dynamic tuple adds an outer
   * offset pointer. Declaring this as a tuple made viem try to follow that non-existent pointer,
   * which landed the decoder on garbage and silently zeroed `netYes`/`netNo` in the UI (even
   * though `yesCount()` / `noCount()` came out fine — hence "counts work but pools are 0").
   *
   * NOTE: The top-level `name` string (below) is an on-chain function name — hashed for the
   * selector, cannot change. Output parameter `name`s and `internalType`s are ABI metadata only;
   * viem uses them as object keys when decoding.
   */
  {
    type: 'function' as const,
    name: 'getMarket',
    inputs: [],
    outputs: [
      { name: '_creator', type: 'address', internalType: 'address' },
      { name: '_tweetHash', type: 'string', internalType: 'string' },
      { name: '_epoch', type: 'uint256', internalType: 'uint256' },
      { name: '_parentMarket', type: 'address', internalType: 'address' },
      { name: '_launchAt', type: 'uint256', internalType: 'uint256' },
      { name: '_expiresAt', type: 'uint256', internalType: 'uint256' },
      { name: '_closeAt', type: 'uint256', internalType: 'uint256' },
      { name: '_netYes', type: 'uint256', internalType: 'uint256' },
      { name: '_netNo', type: 'uint256', internalType: 'uint256' },
      { name: '_totalVotes', type: 'uint256', internalType: 'uint256' },
      { name: '_platformFeeCollected', type: 'uint256', internalType: 'uint256' },
      { name: '_boostAmount', type: 'uint256', internalType: 'uint256' },
      { name: '_state', type: 'uint8', internalType: 'enum AAMarket.MarketState' },
      { name: '_creatorFeeWithdrawn', type: 'bool', internalType: 'bool' },
      { name: '_platformFeeWithdrawn', type: 'bool', internalType: 'bool' },
    ],
    stateMutability: 'view',
  },
  { type: 'function' as const, name: 'yesCount', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  {
    type: 'function' as const,
    name: 'maxVotesSnapshot',
    inputs: [],
    outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }],
    stateMutability: 'view',
  },
  { type: 'function' as const, name: 'noCount', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'netYes', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'netNo', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'boostAmount', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'epoch', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'parentMarket', inputs: [], outputs: [{ name: '', type: 'address', internalType: 'address' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'effectivePlayCost', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'effectiveLaunchCost', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'launchCostSnapshot', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'playCostSnapshot', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'relaunchMultiplierSnapshot', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'maxRelaunchSnapshot', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  {
    type: 'function' as const,
    name: 'getVote',
    inputs: [{ name: '_voter', type: 'address', internalType: 'address' }],
    outputs: [
      { name: '', type: 'tuple', internalType: 'tuple', components: [
        { name: 'side', type: 'uint8', internalType: 'uint8' },
        { name: 'amount', type: 'uint256', internalType: 'uint256' },
        { name: 'pricePaid', type: 'uint256', internalType: 'uint256' },
        { name: 'payout', type: 'uint256', internalType: 'uint256' },
        { name: 'withdrawn', type: 'bool', internalType: 'bool' },
      ]},
    ],
    stateMutability: 'view',
  },
  {
    type: 'function' as const,
    name: 'getCurrentPrices',
    inputs: [],
    outputs: [
      { name: 'priceYay', type: 'uint256', internalType: 'uint256' },
      { name: 'priceNay', type: 'uint256', internalType: 'uint256' },
    ],
    stateMutability: 'view',
  },
  { type: 'function' as const, name: 'state', inputs: [], outputs: [{ name: '', type: 'uint8', internalType: 'uint8' }], stateMutability: 'view' },
  {
    type: 'function' as const,
    name: 'withdrawWindowSnapshot',
    inputs: [],
    outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function' as const,
    name: 'creatorSharePercentSnapshot',
    inputs: [],
    outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }],
    stateMutability: 'view',
  },
  { type: 'function' as const, name: 'getVoters', inputs: [], outputs: [{ name: '', type: 'address[]', internalType: 'address[]' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'getYesProbability', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'canVote', inputs: [{ name: '_voter', type: 'address', internalType: 'address' }], outputs: [{ name: '', type: 'bool', internalType: 'bool' }], stateMutability: 'view' },
  {
    type: 'function' as const,
    name: 'vote',
    inputs: [{ name: '_side', type: 'uint8', internalType: 'uint8' }],
    outputs: [],
    stateMutability: 'payable',
  },
  { type: 'function' as const, name: 'boost', inputs: [], outputs: [], stateMutability: 'payable' },
  { type: 'function' as const, name: 'closeMarket', inputs: [], outputs: [], stateMutability: 'nonpayable' },
  { type: 'function' as const, name: 'withdraw', inputs: [], outputs: [], stateMutability: 'nonpayable' },
  { type: 'function' as const, name: 'withdrawCreatorFee', inputs: [], outputs: [], stateMutability: 'nonpayable' },
  { type: 'function' as const, name: 'withdrawPlatformFee', inputs: [], outputs: [], stateMutability: 'nonpayable' },
  { type: 'function' as const, name: 'expireAndSweep', inputs: [], outputs: [], stateMutability: 'nonpayable' },
  { type: 'function' as const, name: 'closerWallet', inputs: [], outputs: [{ name: '', type: 'address', internalType: 'address' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'closerPrivilegeEndsAt', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  {
    type: 'function' as const,
    name: 'getAllPayouts',
    inputs: [],
    outputs: [
      { name: 'votersList', type: 'address[]', internalType: 'address[]' },
      { name: 'payoutAmounts', type: 'uint256[]', internalType: 'uint256[]' },
      { name: 'withdrawnFlags', type: 'bool[]', internalType: 'bool[]' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function' as const,
    name: 'estimatedPayout',
    inputs: [{ name: 'voter', type: 'address', internalType: 'address' }],
    outputs: [{ name: 'payout', type: 'uint256', internalType: 'uint256' }],
    stateMutability: 'view',
  },
  // Auto-generated getters for public state variables. These exist on-chain
  // (see lib/marketChainRead.ts PER_FIELD_ABI) and several call sites read them
  // individually instead of via getMarket().
  { type: 'function' as const, name: 'creator', inputs: [], outputs: [{ name: '', type: 'address', internalType: 'address' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'tweetHash', inputs: [], outputs: [{ name: '', type: 'string', internalType: 'string' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'launchAt', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'expiresAt', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'closeAt', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'totalVotes', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'factory', inputs: [], outputs: [{ name: '', type: 'address', internalType: 'address' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'config', inputs: [], outputs: [{ name: '', type: 'address', internalType: 'address' }], stateMutability: 'view' },
  { type: 'function' as const, name: 'payoutFactorSnapshot', inputs: [], outputs: [{ name: '', type: 'uint256', internalType: 'uint256' }], stateMutability: 'view' },
] as const;

// IMPORTANT: must go through parseAbi — viem cannot encode calls from raw
// human-readable strings (it throws `TypeError: Cannot use 'in' operator`),
// which made every AAConfig read fail silently and fall back to defaults.
export const AAConfigABI = parseAbi([
  'function playCost() external view returns (uint256)',
  'function launchCost() external view returns (uint256)',
  'function maxVotes() external view returns (uint256)',
  'function MAX_VOTES_CAP() external view returns (uint256)',
  'function expiryTime() external view returns (uint256)',
  'function withdrawWindow() external view returns (uint256)',
  'function platformFeePercent() external view returns (uint256)',
  'function creatorSharePercent() external view returns (uint256)',
  'function relaunchMultiplier() external view returns (uint256)',
  'function maxRelaunch() external view returns (uint256)',
  'function relaunchExpiryBonus() external view returns (uint256)',
  'function payoutFactor() external view returns (uint256)',
  'function closerPrivilegeDuration() external view returns (uint256)',
  'function updateCloserPrivilegeDuration(uint256 _closerPrivilegeDuration) external',
]);

/**
 * Current + historical factory/config pairs. Index 0 is the live deploy used
 * for *new* `createMarket` calls. Older pairs stay listed so markets launched
 * from previous `.sol` deploys keep resolving (relaunch, quotes, config reads).
 *
 * `scripts/update-configs.js` prepends a new pair on redeploy and never drops
 * previous entries. Goldsky / the reconciler watch every factory here.
 */
export type ProtocolDeployment = {
  factory: `0x${string}`;
  config: `0x${string}`;
  /** Inclusive indexer floor for this factory; Goldsky uses the minimum across all entries. */
  fromBlock?: number;
};

// PROTOCOL_DEPLOYMENTS_START
export const PROTOCOL_DEPLOYMENTS: readonly ProtocolDeployment[] = [
  {
    factory: '0x95591cCe51939c256Af21509A0A6Be5914621D0c',
    config: '0x679470975E5e45557F79752AfeC281cd0a970cE3',
    fromBlock: 49606598,
  },
];
// PROTOCOL_DEPLOYMENTS_END

// Contract addresses (Base mainnet - factory + child design). Always the newest pair.
export const CONTRACT_ADDRESSES = {
  AAFactory: PROTOCOL_DEPLOYMENTS[0].factory,
  AAConfig: PROTOCOL_DEPLOYMENTS[0].config,
};

/** Placeholder addresses for launch liquidity votes on AAMarket (payout accrues to platform). */
export const POOL_SEED_VOTER_YAY = '0x000000000000000000000000000000000000A11E' as const;
export const POOL_SEED_VOTER_NAY = '0x000000000000000000000000000000000000A11f' as const;

// Market states (contract enum: Live=0, Closed=1, Expired=2)
export const MARKET_STATES = {
  Live: 0,
  Closed: 1,
  Expired: 2
};

/**
 * Centralized on-chain identifier names (strings) for the "market" ABI surface.
 *
 * These `as const` strings are the EXACT Solidity function/event names in the
 * deployed contracts. wagmi/viem hashes them to compute selectors and event
 * topics, so the runtime value of each string cannot change without a new
 * contract deploy. Callers should import these constants instead of
 * hard-coding the strings directly.
 */
export const ABI_FN_GET_MARKET = 'getMarket' as const;
export const ABI_FN_CREATE_MARKET = 'createMarket' as const;
export const ABI_FN_CLOSE_MARKET = 'closeMarket' as const;
export const ABI_FN_GET_MARKET_BY_TWEET_HASH = 'getMarketByTweetHash' as const;
export const ABI_FN_GET_MARKET_BY_TWEET_HASH_AND_EPOCH = 'getMarketByTweetHashAndEpoch' as const;
export const ABI_FN_GET_ALL_MARKETS = 'getAllMarkets' as const;
export const ABI_FN_PARENT_MARKET = 'parentMarket' as const;
export const ABI_EVENT_MARKET_CLOSED = 'MarketClosed' as const;
export const ABI_EVENT_MARKET_CREATED = 'MarketCreated' as const;

// Vote sides
export const VOTE_SIDES = {
  Yes: 0,
  No: 1
};

// Network configuration
export const NETWORKS = {
  BASE_MAINNET: {
    chainId: 8453,
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    explorer: 'https://basescan.org',
    currency: 'ETH',
  },
} as const

// Constants (ETH-based, epoch 1)
export const CREATOR_DEPOSIT = 0.00015 // launchCost
export const VOTER_DEPOSIT = 0.00005  // playCost
